import type { UserGroup } from '@gmn-portal/backend';
import {
  AlertTriangle,
  Calendar,
  Check,
  CircleDot,
  Contact,
  Fuel,
  Hash,
  Lightbulb,
  MapPin,
  Plane,
  SeparatorHorizontal,
  Stethoscope,
  ToggleLeft,
  TriangleAlert,
  Type,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from '@/core/i18n/use-intl';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui';
import { cn } from '@/shared/utils';
import {
  DEFAULT_CUSTOMER_GROUP_KEYS,
  DEFAULT_GMN_GROUP_KEYS,
  HELI_PROVIDER_GROUP_KEY,
  sortUserGroupsByKey,
} from '@/shared/utils/permissions';
import type {
  FieldPermission,
  FieldPermissions,
  FieldType,
  FieldWidth,
  MetadataField,
  MetadataGroup,
} from './types';

interface MetadataFieldDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  field?: MetadataField | null;
  fields: MetadataField[];
  groups: MetadataGroup[];
  onSave: (field: Partial<MetadataField>) => void;
  userGroups?: UserGroup[];
}

const fieldTypeOptions: {
  value: FieldType;
  icon: React.ComponentType<{ className?: string }>;
  descriptionKey: string;
}[] = [
  {
    value: 'string',
    icon: Type,
    descriptionKey: 'fieldTypeDescriptions.string',
  },
  {
    value: 'number',
    icon: Hash,
    descriptionKey: 'fieldTypeDescriptions.number',
  },
  {
    value: 'boolean',
    icon: ToggleLeft,
    descriptionKey: 'fieldTypeDescriptions.boolean',
  },
  {
    value: 'date',
    icon: Calendar,
    descriptionKey: 'fieldTypeDescriptions.date',
  },
  {
    value: 'contact',
    icon: Contact,
    descriptionKey: 'fieldTypeDescriptions.contact',
  },
  {
    value: 'positions',
    icon: MapPin,
    descriptionKey: 'fieldTypeDescriptions.positions',
  },
  {
    value: 'status',
    icon: CircleDot,
    descriptionKey: 'fieldTypeDescriptions.status',
  },
  {
    value: 'section',
    icon: SeparatorHorizontal,
    descriptionKey: 'fieldTypeDescriptions.section',
  },
];

// Status field icon options - 'none' used instead of '' because Radix Select doesn't allow empty strings
const statusIconOptions = [
  { value: 'none', icon: null, labelKey: 'metadata.status.noIcon' },
  { value: 'fuel', icon: Fuel, labelKey: 'metadata.status.icons.fuel' },
  {
    value: 'helideck',
    icon: Plane,
    labelKey: 'metadata.status.icons.helideck',
  },
  {
    value: 'warning',
    icon: TriangleAlert,
    labelKey: 'metadata.status.icons.warning',
  },
  { value: 'check', icon: Check, labelKey: 'metadata.status.icons.check' },
  {
    value: 'lightning',
    icon: Lightbulb,
    labelKey: 'metadata.status.icons.lightning',
  },
  {
    value: 'medical',
    icon: Stethoscope,
    labelKey: 'metadata.status.icons.medical',
  },
] as const;

type StatusIcon = (typeof statusIconOptions)[number]['value'];

interface StatusOption {
  color: 'red' | 'yellow' | 'green';
  labelDe: string;
  labelEn: string;
}

type GroupMeta = {
  key: string;
  domain: 'gmn' | 'customer';
};

const normalizePermissions = (
  availableGroups: GroupMeta[],
  permissions: FieldPermissions
): FieldPermissions => {
  const mergedKeys = new Set([
    ...availableGroups.map((group) => group.key),
    ...Object.keys(permissions ?? {}),
  ]);
  const normalized: FieldPermissions = {};

  mergedKeys.forEach((key) => {
    if (key === 'gmn-admin') {
      normalized[key] = { read: true, update: true };
      return;
    }
    const entry = permissions?.[key];
    normalized[key] = {
      read: Boolean(entry?.read),
      update: Boolean(entry?.update),
    };
  });

  return normalized;
};

const buildDefaultReadPermissions = (availableGroups: GroupMeta[]): FieldPermissions => {
  return availableGroups.reduce<FieldPermissions>((acc, group) => {
    acc[group.key] = { read: true, update: false };
    return acc;
  }, {});
};

const compactPermissions = (permissions: FieldPermissions): FieldPermissions => {
  const compact: FieldPermissions = {};
  Object.entries(permissions).forEach(([key, value]) => {
    if (key === 'gmn-admin') return;
    if (value.read || value.update) {
      compact[key] = { read: value.read, update: value.update };
    }
  });
  return compact;
};

export function MetadataFieldDialog({
  open,
  onOpenChange,
  field,
  fields,
  groups,
  onSave,
  userGroups,
}: MetadataFieldDialogProps) {
  const { t, locale } = useIntl();
  const isEditing = !!field?.key && !field.key.startsWith('temp-');

  const [type, setType] = useState<FieldType>('string');
  const [labelDe, setLabelDe] = useState('');
  const [labelEn, setLabelEn] = useState('');
  const [permissions, setPermissions] = useState<FieldPermissions>({});
  const [width, setWidth] = useState<FieldWidth>('50');
  const [required, setRequired] = useState(false);
  const [statusLabel, setStatusLabel] = useState('');
  const [statusLabelEn, setStatusLabelEn] = useState('');
  const [groupId, setGroupId] = useState('');

  // Status field specific state
  const [enableYellow, setEnableYellow] = useState(false);
  const [greenLabelDe, setGreenLabelDe] = useState('');
  const [greenLabelEn, setGreenLabelEn] = useState('');
  const [yellowLabelDe, setYellowLabelDe] = useState('');
  const [yellowLabelEn, setYellowLabelEn] = useState('');
  const [redLabelDe, setRedLabelDe] = useState('');
  const [redLabelEn, setRedLabelEn] = useState('');
  const [isGroupIndicator, setIsGroupIndicator] = useState(false);
  const [statusIcon, setStatusIcon] = useState<StatusIcon>('none');
  const [filterFavorite, setFilterFavorite] = useState(false);

  const resolvedGroups = useMemo<GroupMeta[]>(() => {
    if (userGroups && userGroups.length > 0) {
      const orderedGroups = sortUserGroupsByKey(userGroups);
      return orderedGroups.map((group) => ({
        key: group.key,
        domain: group.domain as GroupMeta['domain'],
      }));
    }
    return [
      ...DEFAULT_GMN_GROUP_KEYS.map((key) => ({ key, domain: 'gmn' as const })),
      ...DEFAULT_CUSTOMER_GROUP_KEYS.map((key) => ({ key, domain: 'customer' as const })),
    ];
  }, [userGroups]);

  const gmnGroups = resolvedGroups.filter((group) => group.domain === 'gmn');
  const heliProviderGroups = resolvedGroups.filter(
    (group) => group.key === HELI_PROVIDER_GROUP_KEY
  );
  const customerGroups = resolvedGroups.filter(
    (group) => group.domain === 'customer' && group.key !== HELI_PROVIDER_GROUP_KEY
  );

  // Reset form when field changes
  useEffect(() => {
    if (field) {
      const nextGroupId = groups.some((group) => group.id === field.groupId)
        ? field.groupId
        : groups[0]?.id || '';
      const isTempField = field.key?.startsWith('temp-');
      const basePermissions = isTempField
        ? buildDefaultReadPermissions(resolvedGroups)
        : field.permissions;
      setType(field.type);
      setLabelDe(field.labelDe);
      setLabelEn(field.labelEn);
      setPermissions(normalizePermissions(resolvedGroups, basePermissions));
      setWidth(field.width);
      setRequired(field.required);
      setStatusLabel(field.statusLabel ?? '');
      setStatusLabelEn(field.statusLabelEn ?? '');
      setFilterFavorite(field.filterFavorite ?? false);
      setGroupId(nextGroupId);

      // Status field specific
      const statusOpts = field.statusOptions ?? [];
      const greenOpt = statusOpts.find((o) => o.color === 'green');
      const yellowOpt = statusOpts.find((o) => o.color === 'yellow');
      const redOpt = statusOpts.find((o) => o.color === 'red');
      setGreenLabelDe(greenOpt?.labelDe ?? '');
      setGreenLabelEn(greenOpt?.labelEn ?? '');
      setYellowLabelDe(yellowOpt?.labelDe ?? '');
      setYellowLabelEn(yellowOpt?.labelEn ?? '');
      setRedLabelDe(redOpt?.labelDe ?? '');
      setRedLabelEn(redOpt?.labelEn ?? '');
      setEnableYellow(!!yellowOpt);
      setIsGroupIndicator(field.isGroupIndicator ?? false);
      setStatusIcon((field.icon as StatusIcon) ?? 'none');
    } else {
      setType('string');
      setLabelDe('');
      setLabelEn('');
      setPermissions(
        normalizePermissions(resolvedGroups, buildDefaultReadPermissions(resolvedGroups))
      );
      setWidth('50');
      setRequired(false);
      setStatusLabel('');
      setStatusLabelEn('');

      // Reset status fields
      setEnableYellow(false);
      setGreenLabelDe('');
      setGreenLabelEn('');
      setYellowLabelDe('');
      setYellowLabelEn('');
      setRedLabelDe('');
      setRedLabelEn('');
      setIsGroupIndicator(false);
      setStatusIcon('none');
      setFilterFavorite(false);
      setGroupId(groups[0]?.id || '');
    }
  }, [field, groups, open, resolvedGroups]);

  const existingGroupIndicatorName =
    fields.find(
      (f) =>
        f.groupId === groupId &&
        f.type === 'status' &&
        f.isGroupIndicator === true &&
        f.id !== field?.id
    )?.labelDe ?? null;
  const isGroupIndicatorDisabled = !!existingGroupIndicatorName;

  useEffect(() => {
    if (isGroupIndicatorDisabled && isGroupIndicator) {
      setIsGroupIndicator(false);
    }
  }, [isGroupIndicator, isGroupIndicatorDisabled]);

  const handlePermissionChange = (key: string, next: Partial<FieldPermission>) => {
    setPermissions((prev) => {
      const current = prev[key] ?? { read: false, update: false };
      const merged = { ...current, ...next };
      if (!merged.read) {
        merged.update = false;
      }
      if (merged.update) {
        merged.read = true;
      }
      return { ...prev, [key]: merged };
    });
  };

  const handleSave = () => {
    if (!labelDe.trim()) {
      return;
    }

    const resolvedGroupId = groupId || field?.groupId || groups[0]?.id || '';
    const isFullWidthType =
      type === 'section' || type === 'contact' || type === 'positions' || type === 'status';

    // Build status options if status field
    let statusOptions: StatusOption[] | undefined;
    if (type === 'status') {
      statusOptions = [
        {
          color: 'green',
          labelDe: greenLabelDe.trim() || 'Aktiv',
          labelEn: greenLabelEn.trim() || 'Active',
        },
        ...(enableYellow
          ? [
              {
                color: 'yellow' as const,
                labelDe: yellowLabelDe.trim() || 'Warnung',
                labelEn: yellowLabelEn.trim() || 'Warning',
              },
            ]
          : []),
        {
          color: 'red',
          labelDe: redLabelDe.trim() || 'Inaktiv',
          labelEn: redLabelEn.trim() || 'Inactive',
        },
      ];
    }

    onSave({
      key: field?.key,
      labelDe: labelDe.trim(),
      labelEn: labelEn.trim(),
      type,
      required,
      permissions: compactPermissions(permissions),
      filterFavorite,
      groupId: resolvedGroupId,
      width: isFullWidthType ? '100' : width,
      ...(type === 'positions' && { statusLabel: statusLabel.trim() }),
      ...(type === 'positions' && { statusLabelEn: statusLabelEn.trim() }),
      ...(type === 'status' && { statusOptions }),
      ...(type === 'status' && { isGroupIndicator }),
      ...(type === 'status' && { icon: statusIcon === 'none' ? undefined : statusIcon }),
    });

    onOpenChange(false);
  };

  const renderPermissionTable = (title: string, groupsToRender: GroupMeta[]) => {
    if (groupsToRender.length === 0) return null;

    return (
      <div className="rounded-lg border border-border">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {title}
          </span>
          <div className="grid grid-cols-[80px_80px] gap-2 text-xs text-muted-foreground">
            <span className="text-center">{t('metadata.permissions.read')}</span>
            <span className="text-center">{t('metadata.permissions.update')}</span>
          </div>
        </div>
        <div className="divide-y">
          {groupsToRender.map((group) => {
            const entry = permissions[group.key] ?? { read: false, update: false };
            const isGmnAdmin = group.key === 'gmn-admin';
            const labelKey = `portalRoles.${group.key}`;
            const label = t(labelKey as unknown as never);
            const displayLabel = label === labelKey ? group.key : label;
            return (
              <div
                key={group.key}
                className="grid grid-cols-[1fr_80px_80px] items-center gap-2 px-3 py-2 text-sm"
              >
                <span className={cn('truncate', isGmnAdmin && 'text-muted-foreground')}>
                  {displayLabel}
                </span>
                <div className="flex justify-center">
                  <Checkbox
                    checked={entry.read}
                    onCheckedChange={(checked) =>
                      handlePermissionChange(group.key, { read: Boolean(checked) })
                    }
                    disabled={isGmnAdmin}
                  />
                </div>
                <div className="flex justify-center">
                  <Checkbox
                    checked={entry.update}
                    onCheckedChange={(checked) =>
                      handlePermissionChange(group.key, { update: Boolean(checked) })
                    }
                    disabled={isGmnAdmin}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[780px]! max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('metadata.builder.editField') : t('metadata.builder.createField')}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t('metadata.builder.editFieldDescription')
              : t('metadata.builder.createFieldDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Field Type Selector */}
          {!isEditing && (
            <div className="space-y-2">
              <Label>{t('metadata.fieldType')}</Label>
              <div className="grid grid-cols-2 gap-2">
                {fieldTypeOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setType(option.value)}
                      className={cn(
                        'flex items-center gap-2 p-3 rounded-lg border text-left transition-colors',
                        type === option.value
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-border hover:border-primary/50 hover:bg-muted/40'
                      )}
                    >
                      <Icon className="size-4" />
                      <div className="space-y-0.5">
                        <div className="text-sm font-medium">
                          {t(`metadata.fieldTypes.${option.value}`)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t(`metadata.${option.descriptionKey}`)}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Field Labels */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="metadata-field-name-de">{`${t('metadata.fieldName')} (DE)`}</Label>
              <Input
                id="metadata-field-name-de"
                value={labelDe}
                onChange={(e) => setLabelDe(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="metadata-field-name-en">{`${t('metadata.fieldName')} (EN)`}</Label>
              <Input
                id="metadata-field-name-en"
                value={labelEn}
                onChange={(e) => setLabelEn(e.target.value)}
              />
            </div>
          </div>

          {/* Group and Width */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-2">
              <Label>{t('metadata.groups.group')}</Label>
              <Select value={groupId} onValueChange={setGroupId}>
                <SelectTrigger data-testid="metadata-field-group-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {locale === 'en-US' && group.labelEn ? group.labelEn : group.labelDe}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('metadata.builder.width')}</Label>
              <Select
                value={width}
                onValueChange={(value) => setWidth(value as FieldWidth)}
                disabled={
                  type === 'section' ||
                  type === 'contact' ||
                  type === 'positions' ||
                  type === 'status'
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="33">33%</SelectItem>
                  <SelectItem value="50">50%</SelectItem>
                  <SelectItem value="100">100%</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Required + Favorites */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="metadata-required"
                checked={required}
                onCheckedChange={(checked) => setRequired(Boolean(checked))}
                disabled={['section', 'contact', 'positions', 'status', 'boolean'].includes(type)}
              />
              <Label htmlFor="metadata-required">{t('metadata.required')}</Label>
            </div>
            {['string', 'number', 'date', 'boolean', 'status'].includes(type) && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="metadata-filter-favorite"
                  checked={filterFavorite}
                  onCheckedChange={(checked) => setFilterFavorite(Boolean(checked))}
                  data-testid="metadata-field-filter-favorite"
                />
                <Label htmlFor="metadata-filter-favorite">
                  {t('metadata.builder.filterFavorite')}
                </Label>
              </div>
            )}
          </div>

          {/* Positions Field Specific */}
          {type === 'positions' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="metadata-positions-status-label-de">
                  {`${t('metadata.positions.statusLabel')} (DE)`}
                </Label>
                <Input
                  id="metadata-positions-status-label-de"
                  value={statusLabel}
                  onChange={(e) => setStatusLabel(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="metadata-positions-status-label-en">
                  {`${t('metadata.positions.statusLabel')} (EN)`}
                </Label>
                <Input
                  id="metadata-positions-status-label-en"
                  value={statusLabelEn}
                  onChange={(e) => setStatusLabelEn(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Status Field Specific */}
          {type === 'status' && (
            <div className="space-y-4 rounded-lg border border-border p-4">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <CircleDot className="size-4" />
                {t('metadata.status.title')}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="metadata-status-green-label-de">
                    {`${t('metadata.status.greenLabel')} (DE)`}
                  </Label>
                  <Input
                    id="metadata-status-green-label-de"
                    value={greenLabelDe}
                    onChange={(e) => setGreenLabelDe(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="metadata-status-green-label-en">
                    {`${t('metadata.status.greenLabel')} (EN)`}
                  </Label>
                  <Input
                    id="metadata-status-green-label-en"
                    value={greenLabelEn}
                    onChange={(e) => setGreenLabelEn(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="metadata-enable-yellow"
                  checked={enableYellow}
                  onCheckedChange={(checked) => setEnableYellow(Boolean(checked))}
                />
                <Label htmlFor="metadata-enable-yellow">{t('metadata.status.enableYellow')}</Label>
              </div>

              {enableYellow && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="metadata-status-yellow-label-de">
                      {`${t('metadata.status.yellowLabel')} (DE)`}
                    </Label>
                    <Input
                      id="metadata-status-yellow-label-de"
                      value={yellowLabelDe}
                      onChange={(e) => setYellowLabelDe(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="metadata-status-yellow-label-en">
                      {`${t('metadata.status.yellowLabel')} (EN)`}
                    </Label>
                    <Input
                      id="metadata-status-yellow-label-en"
                      value={yellowLabelEn}
                      onChange={(e) => setYellowLabelEn(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="metadata-status-red-label-de">
                    {`${t('metadata.status.redLabel')} (DE)`}
                  </Label>
                  <Input
                    id="metadata-status-red-label-de"
                    value={redLabelDe}
                    onChange={(e) => setRedLabelDe(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="metadata-status-red-label-en">
                    {`${t('metadata.status.redLabel')} (EN)`}
                  </Label>
                  <Input
                    id="metadata-status-red-label-en"
                    value={redLabelEn}
                    onChange={(e) => setRedLabelEn(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t('metadata.status.selectIcon')}</Label>
                  <Select
                    value={statusIcon}
                    onValueChange={(value) => setStatusIcon(value as StatusIcon)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusIconOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            {opt.icon ? <opt.icon className="size-4" /> : null}
                            {t(opt.labelKey)}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 rounded border border-border px-3 py-2">
                    <Checkbox
                      id="metadata-group-indicator"
                      checked={isGroupIndicator}
                      onCheckedChange={(checked) => setIsGroupIndicator(Boolean(checked))}
                      disabled={isGroupIndicatorDisabled}
                    />
                    <Label htmlFor="metadata-group-indicator">
                      {t('metadata.status.isGroupIndicator')}
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('metadata.status.isGroupIndicatorDescription')}
                  </p>
                  {isGroupIndicatorDisabled && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <AlertTriangle className="size-3" />
                      {t('metadata.status.isGroupIndicatorAlreadyExists', {
                        fieldName: existingGroupIndicatorName ?? '',
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Permissions */}
          <div className="space-y-3">
            <div>
              <Label>{t('metadata.permissions.title')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('metadata.permissions.description')}
              </p>
            </div>
            <div className="grid gap-4">
              {renderPermissionTable(t('metadata.permissions.gmnGroups'), gmnGroups)}
              {renderPermissionTable(t('metadata.permissions.customerGroups'), customerGroups)}
              {renderPermissionTable(
                t('metadata.permissions.heliProviderGroups'),
                heliProviderGroups
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} type="button">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} type="button">
            {isEditing ? t('common.save') : t('metadata.builder.createField')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
